import React from 'react';
import {Text} from 'components/core';
import {getHour, formatBytes, getIconForType} from 'controllers/Utils.js';
import api from 'controllers/raymanAPI.js';
import {FileViewerCtx} from 'components/DialogsContext';
import {UsersCtx} from 'components/dataProviders/users';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/core/styles';
import { Description, CallEnd, Done, DoneAll, PhoneDisabled, PhoneMissed } from '@material-ui/icons';
import {CircularProgress} from '@material-ui/core';
import Image from 'material-ui-image';


const useStyles = makeStyles((theme) => ({
    date: {
        padding: '6px 16px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
        borderRadius: 16,
        height: 32,
        lineHeight: "20px",
        fontSize: 15,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        margin: '5px auto'
    },
    iconInfo: {
        display: 'inline-flex',
        flexDirection: 'row',
        margin: '5px auto',
        fontSize: 15,
        height: 20,
        color: theme.palette.text.secondary,
        '& > *':
        {
            margin: "0 5px",
            lineHeight: "20px"
        },
        '& .MuiSvgIcon-root':
        {
            color: theme.palette.text.primary,
            width: 20,
            height: 20,
            
        }
    },
    timeBlock: {
        opacity: 0.7,
        fontSize: 13
    },
    chatBubble: {
        position: "relative",
        margin: 2,
        marginTop: 5,
        marginRight: 26,
        marginLeft: 26,
        padding: "6px 8px 4px 6px",
        maxWidth: "80%",
        minWidth: 200,
        borderRadius: 8,
        // borderTopRightRadius: 0,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.background.paper,
        // boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.2)",
        filter: "drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.2))",
        '&:before': {
            content:"\"\"",
            position:"absolute",
            right:"-13px",
            top: 0,
            borderTop:`2px solid ${theme.palette.primary.main}`,
            borderBottom: `5px solid ${theme.palette.primary.main}`,
            borderRight:`25px solid ${theme.palette.primary.main}`,
            borderLeft:`5px solid ${theme.palette.primary.main}`,
            borderRadius: 5,
            zIndex: -999
        },
        '&:after': {
            content:"\"\"",
            position:"absolute",
            right:"-12px",
            top: 6,
            borderTop:`14px solid ${theme.palette.primary.main}`,
            borderRight:"14px solid transparent",
            borderLeft:`14px solid ${theme.palette.primary.main}`,
            zIndex: -999
        },
        '&.grouped': {
            marginTop: 0,
            borderRadius: 8,
            '&:before': {
                display: "none"
            },
            '&:after': {
                display: "none"
            }
        },
        '& $content': {

        },
        '& $footer':
        {
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'flex-end',
            '& .MuiSvgIcon-root':
            {
                // color: theme.palette.text.primary,
                width: 13,
                height: 13,
                marginLeft: 5 
            }
        }
    },
    chatRow: {
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        '&.right':
        {
            justifyContent: "flex-end",
            
        },
        '&.left': {
            '& $username': {
                color: theme.palette.primary.main
            },
            '& $chatBubble':
            {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.secondary,
                // borderRadius: 8,
                // borderTopLeftRadius: 0,
                '&:before': {
                    content:"\"\"",
                    position:"absolute",
                    left:"-13px",
                    right: "auto",
                    top: 0,
                    borderTop:`2px solid ${theme.palette.background.paper}`,
                    borderBottom: `5px solid ${theme.palette.background.paper}`,
                    borderLeft:`25px solid ${theme.palette.background.paper}`,
                    borderRight:`5px solid ${theme.palette.background.paper}`,
                    borderRadius: 5
                },
                '&:after': {
                    content:"\"\"",
                    position:"absolute",
                    left:"-12px",
                    right: "auto",
                    top: 6,
                    borderTop:`14px solid ${theme.palette.background.paper}`,
                    borderLeft:"14px solid transparent",
                    borderRight:`14px solid ${theme.palette.background.paper}`,
                },
            },
            '& $fileIcon': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.paper
            }
        },
    },
    content: {marginBottom: 2},
    footer: {},
    username: {
        fontWeight: 600,
        marginBottom: 2
    },
    imageRoot: {
        borderRadius: 8
    },
    spinner: {
        marginTop: 10,
        marginLeft: "calc(50% - 20px)"
    },
    genericFile: {
        display: "flex",
        flexDirection: "row"
    },
    fileIcon: {
        width: 40,
        height: 40,
        padding: 5,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        borderRadius: 6,
        marginRight: 10
    },
    fileInfo: {
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        height: "100%",
        flexWrap: "no-wrap",
        textOverflow: "ellipsis"
    },
    fileName: {

    },
    fileMeta: {
        opacity: 0.7
    }
}), {name: "ChatElements"});

function ImageContent({message, channelID, fileData, onContentUpdated})
{
    const [loadState, setLoadState] = React.useState("loading");
    const viewerCtx = React.useContext(FileViewerCtx);
    const classes = useStyles();
    function handleImageLoaded(evt)
    {
        setLoadState("loaded");
        if(onContentUpdated) onContentUpdated();
    }

    function handleLoadError(evt)
    {
        setLoadState("error");
        if(onContentUpdated) onContentUpdated();
    }

    React.useEffect(() =>
    {
        if(message.content.disabled)
        {
            setLoadState("error");
            if(onContentUpdated) onContentUpdated();
        }
    }, [message, onContentUpdated]);

    function fileClicked()
    {
        viewerCtx.setShowing(fileData);
    }

    const fileSrc = api.channels.files.fileDownloadURL(channelID, fileData.fileID);
    
    return <div className={classes.imageRoot}  onClick={() => fileClicked()}>
        {loadState !== "error" ? 
            <a href={fileSrc} target="_blank" rel="noopener noreferrer"><Image 
            style={{paddingTop: 0, maxWidth: "100%", maxHeight: "100%", width: loadState === "loading" && 50, height: loadState === "loading" && 50}} 
            iconContainerStyle={{transform: 'scale(0.5)'}} 
            imageStyle={{objectFit: "contain", position: "relative", maxWidth: "400px", borderRadius: 4}} 
            color="transparent" 
            src={fileSrc} 
            alt={fileData.fileName}
            onLoad = {handleImageLoaded}
            onError = {handleLoadError}
            /></a>
        : ""
        }
        </div>
}

function GenericFileContent({channelID, fileData})
{
    const viewerCtx = React.useContext(FileViewerCtx);
    const classes = useStyles();

    function fileClicked()
    {
        viewerCtx.setShowing(fileData);
    }
    
    const fileSrc = fileData ? api.channels.files.fileDownloadURL(channelID, fileData.fileID) : null;

    return <div className={classes.genericFile}>
        <Description className={classes.fileIcon}/>
        <div className={classes.fileInfo}>
            {fileData ? <a href={fileSrc} target="_blank" rel="noopener noreferrer">
                <span className={classes.fileName}>{fileData.fileName}</span>
                <span className={classes.fileMeta}>{formatBytes(fileData.bytes)}</span>
            </a>
            :
            <span className={classes.fileName}>{"Archivo Eliminado"}</span>
            }
        </div>
    </div>
}

function UnknownFileContent({message, channelID, onContentUpdated = null})
{
    // const classes = useStyles();

    if(message.content.disabled)
    {
        return <div className="file">
        <GenericFileContent fileData={null} />
        </div>
    }
    else
    {
        return message.content.file.fileType.startsWith("image") ? 
        <ImageContent channelID={channelID} message={message} fileData={message.content.file} onContentUpdated={onContentUpdated}/> 
        : 
        <GenericFileContent fileData={message.content.file} channelID = {channelID} />
    }
}


function ChatBubble({message, position, channelID, time, children, showReadState, grouped})
{
    const classes = useStyles();
    const usersCtx = React.useContext(UsersCtx);

    if(usersCtx.loading) return null;
    return <div className={clsx(classes.chatRow, position)}>
        <div className={clsx(classes.chatBubble, grouped ? "grouped" : "")}>
            {!grouped && message.fromUser && message.fromUser > 0 ?
                <span className={classes.username}>{usersCtx.data.indexed[message.fromUser].name+" "+usersCtx.data.indexed[message.fromUser].surname1}</span>
                : null
            }
            <div className={classes.content}>{children}</div>
            <div className={classes.footer}>
                <Text className={classes.timeBlock}>{time}</Text>
                {showReadState ?
                    message.read ? <DoneAll /> : <Done />
                    : null 
                }
            </div>
        </div>
    </div>
}

function ChatIconInfo({time, icon, children})
{
    const classes = useStyles();
    return <div  className={classes.iconInfo}>
        {icon}
        <Text>{children}</Text>
        <Text className={classes.timeBlock}>{time}</Text>
        </div>
}

function ChatDate({children})
{
    const classes = useStyles();
    return <div className={classes.date}>
        <Text>{children}</Text>
        </div>
}

function ChatItem({message, position = "left", channelID, showReadState = false, grouped = false})
{
    let date = new Date(message.datetime);
    const dateText = getHour(date);
    switch(message.type)
    {
        case 'file':
            return <ChatBubble message={message} position={position} time={dateText} className="fileBubble" showReadState={showReadState} grouped={grouped}>
                    <UnknownFileContent message={message} channelID={channelID}/>
                </ChatBubble>
        case 'call':
            switch(message.content.status)
            {
                case 'rejected':
                return <ChatIconInfo time={dateText} icon={<PhoneDisabled />}> Llamada rechazada </ChatIconInfo>
                case 'missed':
                return <ChatIconInfo time={dateText} icon={<PhoneMissed />}> Llamada sin respuesta </ChatIconInfo>
                case 'error':
                return <ChatIconInfo time={dateText} icon={<PhoneDisabled />}> Error en la llamada </ChatIconInfo>
                case 'ended':
                    let callTimeStr = "";
                    let time = message.content.totalTime;
                    if(time >= 3600)
                    {
                        callTimeStr += " "+Math.floor(message.content.totalTime/3600)+" h";
                        time = time % 3600;
                    }
                    if(time >= 60)
                    {
                        callTimeStr += " "+Math.floor(message.content.totalTime/60)+" min";
                        time = time % 60;
                    }
                    if(time > 0)
                    {
                        callTimeStr += " "+Math.round(time)+" sec";
                    }
                    callTimeStr += " ";

                return <ChatIconInfo time={dateText} icon={<CallEnd />}> Llamada finalizada ({callTimeStr}) </ChatIconInfo>

                default: break;
            }
            break;
        case 'text':
            return <ChatBubble message={message} position={position} time={dateText} showReadState={showReadState} grouped={grouped}>
                {message.content}
            </ChatBubble>
        default:
        return <></>;
    }
}

export default ChatItem;
export {ChatItem, ChatIconInfo, ChatDate};